/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from '@mui/material/Card'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDAlert from 'components/MDAlert'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDButton from 'components/MDButton'
import { Tooltip, IconButton, Modal, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material'

import CrudService from 'services/cruds-service'
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import moment from 'moment'
import { DataGrid } from '@mui/x-data-grid'
import MDInput from 'components/MDInput'
import JsonTree from 'cruds/campaign/modal/jsontree'

function EventLogManagement() {
  let { state } = useLocation()

  const [data, setData] = useState([])
  const [tableData, setTableData] = useState([])
  const [notification, setNotification] = useState({
    value: false,
    text: '',
  })
  const [paramTableData, setParamTableData] = useState()
  const [openViewModal, setOpenViewModal] = useState(false)
  const [loader, setLoader] = useState(false)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  useEffect(() => {
    document.title = `Rivio | Event Logs`
  }, [])

  useEffect(() => {
    setTableData(getRows(data))
  }, [data])

  const callApi = async () => {
    setLoader(true)
    const sortField = sortModel[0]?.field || "eventdate";
    const sortOrder = sortModel[0]?.sort || "DESC";
    const response = await CrudService.getEventLogs({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      sortField,
      sortOrder,
      search: searchQuery.toLowerCase()
    })
    // if (response.success) {
    setData(response.data);
    setRowCount(response.count);
    // }
    setLoader(false)
  }

  useEffect(() => {
    callApi();
  }, [paginationModel, sortModel]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery !== "") {
        callApi();
      }
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery])

  const searchHandler = async (value) => {
    setSearchQuery(value)
    if (value === "") {
      const response = await CrudService.getEventLogs({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: value.toLowerCase()
      })
      // if (response.success) {
      setData(response.data);
      setRowCount(response.count);
    }
  }

  async function deleteHandler() {
    try {
      if (confirm("Are you sure you want to delete these event logs?")) {
        const response = await CrudService.deleteEventLogs(rowSelectionModel)
        if (response.success) {
          callApi();
          setRowSelectionModel([])
          setNotification({
            value: true,
            text: "Event logs deleted successfully",
          })
        }
        else {
          setNotification({
            value: true,
            text: "Error deleting event logs",
          })
        }
      }
    } catch (error) {
      console.error('deleteHandler', error)
      setNotification({
        value: true,
        text: "Error deleting event logs",
      })
    }
  }

  useEffect(() => {
    if (!state) return
    setNotification({
      value: state.value,
      text: state.text,
    })
  }, [state])

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: '',
        })
      }, 5000)
    }
  }, [notification])

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      return {
        id: row.id,
        'user.name': row.attributes.user?.name,
        content: row.attributes.content,
        request: row.attributes.request ? row.attributes.request : '',
        response: row.attributes.response ? row.attributes.response : '',
        eventdate: row.attributes.eventdate,
        'eventType.name': row.attributes.EventType?.name,
      }
    })
    return updatedInfo
  }

  const clickViewHandler = (value) => {
    // nfo.cell.row.original.id,
    //                 info.cell.row.original,
    setOpenViewModal(true)
    const filteredValue = Object.keys(value).reduce((acc, key) => {
      if (key !== 'map_status') {
        acc[key] = value[key]
      }
      return acc
    }, {})

    // Set the filtered data in the state
    setParamTableData(filteredValue)
  }

  const handleViewClose = () => {
    setOpenViewModal(false)
  }

  const dataTableData = {
    columns: [
      { headerName: 'ID', field: 'id', width: 70 },
      { headerName: 'Event Type', field: 'eventType.name', width: 200 },
      { headerName: 'User', field: 'user.name', width: 200 },
      { headerName: 'Content', field: 'content', width: 300 },
      {
        headerName: 'Request',
        field: 'request',
        width: 400,
        renderCell: (info) => {
          if(info.value.display_ui){
            return (
              <MDTypography variant="body2" title={info.value.display_ui}>
                {info.value.display_ui}
              </MDTypography>
            )
          }
          const request = info.value

          return (
            <div>
              {request?.device_data?.id} : {request?.deviceMonitor?.name}
            </div>
          )
        },
      },

      {
        headerName: 'Response',
        field: 'response',
        width: 500,
        renderCell: (info) => {
          return (
            <div
              style={{
                whiteSpace: 'normal',
                // maxWidth: '250px',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                width: '100%',
              }}
            >
              {info.value}
            </div>
          )
        },
      },
      {
        headerName: 'Event Date',
        field: 'eventdate',
        width: 250,
        isSortedDesc: true,
        isSorted: true,
        renderCell: (info) => {
          return (
            <div
              style={{
                whiteSpace: 'normal',
                maxWidth: '300px',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              {moment(info.value).format(
                'MMMM DD, YYYY h:mm:ss A',
              )}
            </div>
          )
        },
      },
      {
        headerName: 'Target',
        field: '',
        sortable: false,
        renderCell: (info) => {
          return (
            <div>

              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                type="submit"
                onClick={() =>
                  clickViewHandler(
                    info.row
                  )
                }
              >
                View
              </MDButton>
            </div>
          )
        },
      },
    ],

    rows: tableData,
  }

  const renderTableCell = (data) => {
    // If the data is an object (JSON), render each key-value pair
    if (typeof data === 'object' && data !== null) {
      return (
        <Table>
          <TableBody>
            {Object.entries(data).map(([key, value]) => (
              <TableRow key={key}>
                <TableCell>{key}</TableCell>
                <TableCell>{renderTableCell(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )
    }
    return data
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert color="info" my="20px">
          <MDTypography variant="body2" color="white">
            {notification.text || ''}
          </MDTypography>
        </MDAlert>
      )}
      <MDBox pt={1}>
        <MDBox>
          <Card>
            <MDBox mb={2} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {
                rowSelectionModel.length > 0 && (
                  <MDButton variant="gradient" color="error" size="small" type="submit" onClick={() => deleteHandler()}>Delete</MDButton>
                )
              }
              <MDInput
                type="text"
                label="Search"
                variant="outlined"
                onChange={(e) => searchHandler(e.target.value)}
                value={searchQuery}
              />
            </MDBox>
            <DataGrid
              rows={tableData}
              columns={dataTableData.columns}
              getRowHeight={() => 'auto'}
              disableColumnFilter
              disableRowSelectionOnClick
              checkboxSelection
              pagination
              paginationMode="server"
              sortingMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              pageSizeOptions={[10, 25, 50, 100]} // ✅ Add this
              onSortModelChange={(model) => setSortModel(model)}
              onPaginationModelChange={(newPage) => setPaginationModel(newPage)}
              sx={{ color: '#fff', maxHeight: 'calc(100vh - 195px)' }}
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel)
              }}
              loading={loader}
            // rowSelectionModel={rowSelectionModel}
            />
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
      <JsonTree open={openViewModal} onClose={handleViewClose} data={paramTableData} />
      {/* <Modal
        open={openViewModal}
        onClose={handleViewClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Card
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%', // Set the desired width here
            // backgroundColor: 'white',
            boxShadow: 24,
            // p: 4,
            maxHeight: '70vh', // Set max height to enable scrolling
            overflowY: 'auto',
            overflowX: 'AUTO',
          }}
        >
          <DynamicDataTable EventData={paramTableData} />
        </Card>
      </Modal> */}
    </DashboardLayout>
  )
}

export default EventLogManagement
